import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { format } from "date-fns/format"
import { Trash2, Weight } from "lucide-react"
import { DateTime } from "luxon"
import type React from "react"
import { useState } from "react"
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Skeleton } from "../components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table"
import { Tooltip } from "../components/ui/tooltip"
import { apiFetch } from "../utils/apiFetch"

export const Route = createFileRoute("/_app/weight")({
  component: BodyWeightTracker,
})

export interface BodyWeightRecord {
  weighInId: string
  userId: string
  weight: number
  status: string
  dateCreated: string
}

type TNewWeighIn = {
  weight: number
  date: DateTime
}

export default function BodyWeightTracker() {
  const { data: weights, isLoading } = useQuery({
    queryKey: ["weighIns"],
    queryFn: async () => {
      return await apiFetch<Array<BodyWeightRecord>>("/v1/weighIns", {
        method: "GET",
      })
    },
  })
  const { mutateAsync } = useMutation({
    mutationFn: async (newRecord: TNewWeighIn) => {
      return await apiFetch<TNewWeighIn>("/v1/weighIns", {
        method: "POST",
        body: JSON.stringify(newRecord),
      })
    },
    onSuccess: (_data) => {
      setWeight("")
    },
  })
  const { mutateAsync: deleteAsync } = useMutation({
    mutationFn: async (weighInId: string) => {
      return await apiFetch<BodyWeightRecord>(`/v1/weighIns/${weighInId}`, {
        method: "delete",
      })
    },
  })
  const [weight, setWeight] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const weightValue = parseFloat(weight)
    if (isNaN(weightValue) || weightValue <= 0) {
      return
    }

    await mutateAsync({ weight: weightValue, date: DateTime.utc() })
  }

  const chartData = (weights ?? []).map((record) => ({
    date: format(record.dateCreated, "MMM dd"),
    weight: record.weight,
  }))

  return (
    <Card className="workout-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Weight className="h-5 w-5" />
          Body Weight Tracker
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="flex items-start gap-2">
          <div className="flex-1 space-y-2">
            <Input
              type="number"
              min="0"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Enter your weight in lbs"
              className="w-full"
            />
          </div>
          <Button type="submit">Log Weight</Button>
        </form>

        {isLoading ? (
          <div className="space-y-6">
            <Skeleton className="h-64 w-full" />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Date</TableHead>
                  <TableHead>Weight (lbs)</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell className="py-7 font-medium">
                      <Skeleton className="w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="w-full" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : chartData.length > 0 ? (
          <div className="space-y-6">
            <div className="h-56 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis
                    domain={["dataMin - 5", "dataMax + 5"]}
                    tick={{ fontSize: 11 }}
                    label={{
                      value: "Weight (lbs)",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 12 },
                    }}
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="weight" stroke="#8884d8" activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px] sm:w-[200px]">Date</TableHead>
                    <TableHead>Weight</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {weights?.toReversed().map((weight) => (
                    <TableRow key={weight.weighInId}>
                      <TableCell className="font-medium text-xs sm:text-sm">
                        {DateTime.fromISO(weight.dateCreated).toLocaleString(DateTime.DATETIME_MED)}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">{weight.weight.toFixed(1)} lbs</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:bg-red-100/10 hover:text-red-700 sm:h-8 sm:w-8"
                          onClick={() => deleteAsync(weight.weighInId)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="py-10 text-center">
            <Weight className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground">No weight data recorded yet.</p>
            <p className="text-muted-foreground text-sm">Log your weight to start tracking your progress.</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
